import { Logger } from './core/logger';
import { LoggerEnvironment } from './core/config';
import { configFromEnv } from './core/runtimes/env';
import { isBrowser, isProd } from './core/runtimes/detect';
import { RuntimeLoggerConfigProvider, validateRuntimeConfig } from './core/runtimes/config';
import { LogLevelLogger, LogLevelConfig } from './loglevel/logger';
import {
  LogLevelRuntimeConfig,
  LogLevelRuntimeConfigs,
  LogLevelRuntimeConfigValidators,
  LogLevelEnvToConfigMapper,
} from './loglevel/runtimes';

const UNKNOWN_SERVICE = 'UnknownService';
const VALID_DEVELOPMENT_ENVS = [LoggerEnvironment.DEVELOPMENT, LoggerEnvironment.TEST];

/**
 * createLoggerFromConfig is a helper function to create Logger instance
 * from a RuntimeLoggerConfigProvider
 *
 * This is used to add any default envs to the logger configuration.
 */
const createLoggerFromConfig = (
  fn: RuntimeLoggerConfigProvider<LogLevelRuntimeConfig, LogLevelConfig>,
  conf: LogLevelRuntimeConfig
): Logger => {
  const resolvedConf = fn(conf);
  return new LogLevelLogger(resolvedConf);
};

/**
 * CreateFallbackLoggerOptions describes the required parameters needed to
 * initialize a fallback logger.
 */
type CreateFallbackLoggerOptions = {
  message: string | Error;
  validationErrors?: Record<string, string | null>;
};

/**
 * createFallbackLogger is used to create a logger when createLogger fails for whatever
 * reason. This keeps code working and stops apps from crashing even if the logger
 * has been misconfigured.
 */
const createFallbackLogger = ({ message, validationErrors = {} }: CreateFallbackLoggerOptions): Logger => {
  const logger = createLoggerFromConfig(LogLevelRuntimeConfigs.fallback, {
    environment: LoggerEnvironment.UNKNOWN,
    service: UNKNOWN_SERVICE,
  });

  logger.error(message, {
    logType: 'LoggerError',
    validationErrors,
  });

  return logger;
};

/**
 * CreateLoggerOptions contains the configuration required to call `createLogger`
 */
type CreateLoggerOptions = LogLevelRuntimeConfig;

/**
 * createLogger returns a new serverless or browser logger based on the provider
 * configuration and current env. If it is not prod, it returns a basic console
 * logger instead.
 */
const createLogger = (conf?: CreateLoggerOptions) => {
  // At runtime, we cannot be sure all values are present even if consumers passed them directly
  let maybeConf = conf as Partial<CreateLoggerOptions>;

  try {
    if (maybeConf === undefined) {
      maybeConf = configFromEnv(LogLevelEnvToConfigMapper);
    }

    const { isValid, validationErrors } = validateRuntimeConfig(maybeConf, LogLevelRuntimeConfigValidators);

    if (isValid === false) {
      return createFallbackLogger({ message: 'Invalid configuration', validationErrors });
    }

    // Because validateLoggerOptions was successful, we can assume it's a concrete
    // instance.
    const validConf = maybeConf as LogLevelConfig;

    // If not isProd, _always_ return the dev or test logger. This is to avoid
    // polluting non-local log streams.
    if (isProd() === false) {
      // Overwrite the environment value unless it's DEV or TEST.
      const origEnv = validConf.environment;
      validConf.environment = VALID_DEVELOPMENT_ENVS.includes(origEnv) ? origEnv : LoggerEnvironment.DEVELOPMENT;

      const logger = createLoggerFromConfig(
        origEnv == LoggerEnvironment.TEST ? LogLevelRuntimeConfigs.test : LogLevelRuntimeConfigs.dev,
        validConf
      );
      logger.info('Development environment detected. Using development configuration.');

      return logger;
    }

    return createLoggerFromConfig(
      isBrowser() ? LogLevelRuntimeConfigs.browser : LogLevelRuntimeConfigs.serverless,
      validConf
    );
  } catch (e) {
    const logger = createFallbackLogger({
      message: e as Error,
      validationErrors: {},
    });
    return logger;
  }
};

export type { Logger, CreateLoggerOptions };
export { createLogger, LoggerEnvironment as Environments, UNKNOWN_SERVICE };
export type { LogEntry, LogEntryMeta, RequestLogEntry } from './core/logEntry';
export { newLogEntry } from './core/logEntry';
export { LogLevel } from './core/levels';
export { useFetchInterceptor } from './fetch';
